import React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Bell, CalendarCheck, CalendarX, CheckCheck, ClipboardCheck, Megaphone, Unlock, UserPlus, Workflow, X } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { useOptionalChat, OPEN_NOTIFICATIONS_EVENT } from '@/components/features/chat/ChatProvider'
import { NOTIFICATION_EVENT, useRealtime } from '@/lib/realtime/RealtimeProvider'
import { SIDE_BACKDROP_CLASSES, SIDE_PANEL_CLASSES, useSwipeToClose } from '@/components/features/chat/sidePanel'
import { SCROLL_CLASSES } from '@/components/features/chat/ChatConversationView'
import { InfiniteSentinel } from '@/components/ui/infinite-sentinel'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { useMenu } from '@/lib/MenuContext'
import { useRoute } from '@/lib/RouteContext'
import { sdk } from '@/lib/sdk'
import { ROUTES } from '@/lib/routes'
import { cn, formatDistanceToNow } from '@/lib/utils'
import { SettingsAlertNotice } from './SettingsAlertNotice'
import { SETTINGS_ALERT_TYPES } from './settingsAlerts'
import type { AppNotification } from '@/lib/sdk-official/resources/notifications'
import { unlockedMessageKey } from '@/lib/unlockedNotice'
import { useProfileContext } from '@/components/features/profile/ProfileProvider'
import { BadgeArt } from '@/components/features/badges/BadgeChip'
import type { MemberBadge } from '@/types/badges'

/** The types this app knows how to show; anything else the API adds later is skipped. */
const UPVOTE_TYPES = ['upvotes.topicCreated', 'upvotes.statusChanged', 'upvotes.reopened', 'upvotes.merged']
const CHANNEL_TYPES = ['channel.mention', 'channel.reply']
const ONE_ON_ONE_TYPES = ['oneOnOnes.logged']
/* F-141 phase 5: a workflow failing too often (owner and admin), and a workflow split into one per trigger (its creator).
   F-144: the month's fixed-IP HTTP calls ran out (owners, `data.cap`; about the community, not one workflow). */
const WORKFLOW_HTTP_CAP_TYPE = 'workflows.httpFixedIpCapReached'
const WORKFLOW_TYPES = ['workflows.errorRateHigh', 'workflows.splitByTrigger', 'workflows.senderActivated', WORKFLOW_HTTP_CAP_TYPE]
/* F-148: "você recebeu o badge X" (`data.badgeId`); the app reads the badge from the person's own list. */
const BADGE_TYPES = ['badges.awarded']
/** F-150: "new requests to join", grouped (owner and admin). */
const SIGNUP_REQUEST_TYPE = 'signupRequests.received'
/** F-144 A2: a notice from the team or a workflow's "Enviar aviso" (`data.title`, `data.body`: the team's own text). */
const TEAM_NOTICE_TYPE = 'team.notice'
/** F-151a: a badge or a level (`data.source`, APP-194) opened a space or a section (`entityType` space with `data.spaceId`/`data.sectionId`, or section with `data.sectionId`). */
const UNLOCKED_TYPE = 'spaces.unlocked'
/** F-164: a scheduled post went out, or could not (`entityType` content|status, `entityId`, `data.spaceId`, `data.title`). */
const POST_PUBLISHED_TYPE = 'posts.scheduledPublished'
const POST_FAILED_TYPE = 'posts.scheduleFailed'
/** F-248: "Lembrar quem não confirmou" (`entityType` content, `entityId`, `data.title`, `data.spaceId`, `data.spaceSlug`, `data.sectionSlug`). */
const POST_ACK_REMINDER_TYPE = 'posts.ackReminder'
const KNOWN_TYPES = new Set([POST_ACK_REMINDER_TYPE, 'chat.connectionRequested', 'chat.connectionAccepted', ...UPVOTE_TYPES, ...CHANNEL_TYPES, ...ONE_ON_ONE_TYPES, ...WORKFLOW_TYPES, ...BADGE_TYPES, SIGNUP_REQUEST_TYPE, TEAM_NOTICE_TYPE, UNLOCKED_TYPE, POST_PUBLISHED_TYPE, POST_FAILED_TYPE, ...SETTINGS_ALERT_TYPES])
const isUpvote = (type: string) => type.startsWith('upvotes.')
const isChannel = (type: string) => type.startsWith('channel.')
const isOneOnOne = (type: string) => type.startsWith('oneOnOnes.')
/** `YYYY-MM-DD` as the reader says a day and month. */
const dayMonth = (day: unknown, locale: string) => {
  if (typeof day !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return ''
  const [y, m, d] = day.split('-').map(Number)
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)))
}

const messageOf = (err: unknown): string | undefined =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message

/**
 * In-app notifications (APP-126: the old bell called a route that did not
 * exist; F-108 brought `/api/notifications`). Header bell with the unread
 * count, a panel with the same width rules as the chat (< 600 px full
 * screen, a right sidebar from 600 px up), live through the platform socket
 * (F-110, chat on or off) and on the slow fallback poll while it is down.
 */
export const NotificationBell = () => {
  const { t, i18n } = useTranslation('notifications')
  const { isAuthenticated, user } = useAuth()
  const { community } = useCommunity()
  const chat = useOptionalChat()
  const { openSections: sections, refresh: refreshMenu } = useMenu()
  const { navigate } = useRoute()
  const [open, setOpen] = React.useState(false)
  const [items, setItems] = React.useState<AppNotification[]>([])
  const [cursor, setCursor] = React.useState<string | null>(null)
  const [unread, setUnread] = React.useState(0)
  const [state, setState] = React.useState<'idle' | 'loading' | 'ready' | 'failed'>('idle')
  const [loadingMore, setLoadingMore] = React.useState(false)
  const [busyId, setBusyId] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const swipe = useSwipeToClose(() => setOpen(false))
  const sessionKey = `${community?.id || ''}:${user?.id || ''}`

  const refreshCount = React.useCallback(async () => {
    try {
      setUnread(await sdk.notifications.unreadCount())
    } catch {
      /* the badge keeps its last value */
    }
  }, [])

  const load = React.useCallback(async () => {
    setState('loading')
    try {
      const page = await sdk.notifications.list({ limit: 20 })
      setItems(page.items)
      setCursor(page.nextCursor)
      setUnread(page.unreadCount)
      setState('ready')
    } catch {
      setState('failed')
    }
  }, [])

  /* The count on sign-in and community change; then the socket, or the fallback poll while it is down. */
  React.useEffect(() => {
    setItems([])
    setCursor(null)
    setState('idle')
    if (!isAuthenticated || !community?.id) return
    refreshCount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey, isAuthenticated])

  /* Missed while the socket was down (reconnect), or no socket at all (fallback): one refetch. */
  useRealtime([], () => {
    refreshCount()
    if (open) load()
  }, { enabled: Boolean(isAuthenticated && community?.id), topics: [] })

  React.useEffect(() => {
    if (open) load()
  }, [open, load])

  /* Live: notification.new / read / removed, relayed by RealtimeProvider. */
  React.useEffect(() => {
    const onEvent = (event: Event) => {
      const { type, data } = (event as CustomEvent<{ type: string; data: Record<string, unknown> }>).detail || { type: '', data: {} }
      if (type === 'notification.new') {
        const n = data as unknown as AppNotification
        if (!n?.id) return
        setItems((list) => (list.some((x) => x.id === n.id) ? list : [n, ...list]))
        if (!n.read) setUnread((c) => c + 1)
        /* What just opened belongs in the menu now, without its padlock. */
        if (n.type === UNLOCKED_TYPE) void refreshMenu({ silent: true })
      } else if (type === 'notification.read') {
        const ids = Array.isArray(data.ids) ? (data.ids as string[]) : []
        setItems((list) => list.map((x) => (data.all || ids.includes(x.id) ? { ...x, read: true } : x)))
        if (typeof data.unreadCount === 'number') setUnread(data.unreadCount)
      } else if (type === 'notification.removed') {
        setItems((list) => {
          const gone = list.find((x) => x.id === data.id)
          if (gone && !gone.read) setUnread((c) => Math.max(0, c - 1))
          return list.filter((x) => x.id !== data.id)
        })
      }
    }
    window.addEventListener(NOTIFICATION_EVENT, onEvent)
    return () => window.removeEventListener(NOTIFICATION_EVENT, onEvent)
  }, [refreshMenu])

  /* `/_/chat/connections` (the connection request e-mail) opens the requests here. */
  React.useEffect(() => {
    const onOpen = () => setOpen(true)
    window.addEventListener(OPEN_NOTIFICATIONS_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_NOTIFICATIONS_EVENT, onOpen)
  }, [])

  React.useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  /* F-148: the badges of the notices, read once from the person's own list while the panel shows one. */
  const { openProfile } = useProfileContext()
  const [myBadges, setMyBadges] = React.useState<Map<string, MemberBadge['badge']> | null>(null)
  const missingBadge = open && items.some((n) => BADGE_TYPES.includes(n.type) && typeof n.data?.badgeId === 'string' && !myBadges?.has(n.data.badgeId))
  React.useEffect(() => {
    if (!missingBadge || !user?.id) return
    let alive = true
    sdk.badges.ofMember(user.id)
      .then((list) => { if (alive) setMyBadges(new Map(list.map((g) => [g.badge.id, g.badge]))) })
      .catch(() => { if (alive) setMyBadges(new Map()) })
    return () => { alive = false }
  }, [missingBadge, user?.id, items.length])
  if (!isAuthenticated) return null

  const markRead = (n: AppNotification) => {
    if (n.read) return
    setItems((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)))
    setUnread((c) => Math.max(0, c - 1))
    sdk.notifications.markRead(n.id).catch(() => {})
  }

  const markAll = () => {
    setItems((list) => list.map((x) => ({ ...x, read: true })))
    setUnread(0)
    sdk.notifications.markAllRead().catch(() => {})
  }

  const loadMore = async () => {
    if (!cursor || loadingMore) return
    setLoadingMore(true)
    try {
      const page = await sdk.notifications.list({ limit: 20, cursor })
      setItems((list) => [...list, ...page.items.filter((n) => !list.some((x) => x.id === n.id))])
      setCursor(page.nextCursor)
    } catch {
      setError(t('loadError'))
    } finally {
      setLoadingMore(false)
    }
  }

  /* Accepting or declining removes the notification on the server; it goes here too. */
  const answer = async (n: AppNotification, accept: boolean) => {
    const connectionId = n.data?.connectionId
    if (!connectionId) return
    setBusyId(n.id)
    setError(null)
    try {
      if (accept) await sdk.chat.acceptConnection(connectionId)
      else await sdk.chat.declineConnection(connectionId)
      setItems((list) => list.filter((x) => x.id !== n.id))
      if (!n.read) setUnread((c) => Math.max(0, c - 1))
      chat?.refreshConversations()
    } catch (err) {
      setError(messageOf(err) || t('actionError'))
    } finally {
      setBusyId(null)
    }
  }

  const openChat = async (n: AppNotification) => {
    markRead(n)
    const profileId = n.data?.profileId || n.actor?.id
    if (!chat?.enabled || !profileId) return
    try {
      /* Below 600 px the keyboard would cover the history the person came to read: no cursor there. */
      await chat.openWith(profileId, n.actor ? { ...n.actor } : undefined, n.type === 'chat.connectionAccepted' ? 'connected' : undefined, { focus: window.innerWidth >= 600 })
      if (window.innerWidth >= 1024 && chat.panelState === 'COLLAPSED') chat.setPanelState('EXPANDED')
      setOpen(false)
    } catch (err) {
      setError(messageOf(err) || t('actionError'))
    }
  }

  /* F-139: an Upvote notice carries ids only; the topic opens inside its board. */
  const openTopic = (n: AppNotification) => {
    markRead(n)
    const spaceId = typeof n.data?.spaceId === 'string' ? n.data.spaceId : null
    const topicId = typeof n.data?.topicId === 'string' ? n.data.topicId : n.entityId
    for (const section of sections) {
      const space = section.spaces?.find((sp) => sp.id === spaceId)
      if (space && topicId) {
        setOpen(false)
        navigate(`/${section.slug}/${space.slug}/${topicId}`)
        return
      }
    }
  }

  /* F-138: a mention or a reply opens the channel at that message; a reply (or a mention inside a thread) opens its thread. */
  const channelSpace = (n: AppNotification) => {
    const spaceId = typeof n.data?.spaceId === 'string' ? n.data.spaceId : null
    for (const section of sections) {
      const space = section.spaces?.find((sp) => sp.id === spaceId)
      if (space) return { section, space }
    }
    return null
  }

  const openChannelMessage = (n: AppNotification) => {
    markRead(n)
    const found = channelSpace(n)
    const rootId = typeof n.data?.rootId === 'string' ? n.data.rootId : null
    const messageId = typeof n.data?.messageId === 'string' ? n.data.messageId : n.entityId
    if (!found || !messageId) return
    setOpen(false)
    navigate(rootId
      ? `/${found.section.slug}/${found.space.slug}/${rootId}?thread=${rootId}`
      : `/${found.section.slug}/${found.space.slug}/${messageId}`)
  }

  /* F-136: "Fulano registrou a 1:1 de vocês" opens that meeting in the pair's history (ids and the day only, never a note). */
  const openOneOnOne = (n: AppNotification) => {
    markRead(n)
    const found = channelSpace(n)
    const meetingId = typeof n.data?.meetingId === 'string' ? n.data.meetingId : n.entityId
    if (!found || !meetingId || !n.actor?.id) return
    setOpen(false)
    navigate(`/${found.section.slug}/${found.space.slug}/${n.actor.id}?meeting=${encodeURIComponent(meetingId)}`)
  }

  /* F-141: a workflow notice opens that workflow (Administração › Workflows). */
  const openWorkflow = (n: AppNotification) => {
    markRead(n)
    const id = typeof n.data?.workflowId === 'string' ? n.data.workflowId : (n.type === WORKFLOW_HTTP_CAP_TYPE ? null : n.entityId)
    setOpen(false)
    navigate(id ? `/workflows?workflow=${encodeURIComponent(id)}` : '/workflows')
  }

  const workflowText = (n: AppNotification) => {
    /* SEC-256: someone else on the team turned on a version that speaks in this person's name. */
    if (n.type === 'workflows.senderActivated') {
      const workflowName = typeof n.data?.workflowName === 'string' ? n.data.workflowName : ''
      return t('types.workflowSenderActivated', { actor: n.actor?.name || t('someone'), workflowName, version: Number(n.data?.versionNumber) || '' })
    }
    if (n.type === WORKFLOW_HTTP_CAP_TYPE) return t('types.workflowHttpFixedIpCap', { cap: Number(n.data?.cap) || '' })
    if (n.type === 'workflows.splitByTrigger') {
      const copies = Array.isArray(n.data?.copies) ? n.data.copies.length : 0
      return t('types.workflowSplitByTrigger', { count: copies })
    }
    const counts = { rate: Math.round(Number(n.data?.rate) || 0), failed: Number(n.data?.failed) || 0, runs: Number(n.data?.runs) || 0 }
    /* Phase 5g: the notice names the workflow (older notices came without the name). */
    const name = typeof n.data?.workflowName === 'string' ? n.data.workflowName.trim() : ''
    return name ? t('types.workflowErrorRateHighNamed', { ...counts, name }) : t('types.workflowErrorRateHigh', counts)
  }

  const channelText = (n: AppNotification, name: string) => {
    const channel = channelSpace(n)?.space.title || ''
    return n.type === 'channel.mention' ? t('types.channelMention', { name, channel }) : t('types.channelReply', { name, channel })
  }

  const upvoteText = (n: AppNotification, name: string) => {
    if (n.type === 'upvotes.topicCreated') return n.data?.pending ? t('types.upvoteCreatedPending', { name }) : t('types.upvoteCreated', { name })
    if (n.type === 'upvotes.statusChanged') return t('types.upvoteStatusChanged', { status: t(`upvoteStatus.${String(n.data?.status || 'OPEN')}`) })
    if (n.type === 'upvotes.reopened') return t('types.upvoteReopened')
    return t('types.upvoteMerged')
  }

  const shown = items.filter((n) => KNOWN_TYPES.has(n.type))

  /* F-151a: the space or section a badge opened, by the menu (the notice carries ids only). */
  const unlockedTarget = (n: AppNotification) => {
    const spaceId = n.entityType === 'space' && typeof n.data?.spaceId === 'string' ? n.data.spaceId : null
    const sectionId = typeof n.data?.sectionId === 'string' ? n.data.sectionId : (n.entityType === 'section' ? n.entityId : null)
    for (const section of sections) {
      if (spaceId) {
        const space = section.spaces?.find((sp) => sp.id === spaceId)
        if (space) return { name: space.title, path: `/${section.slug}/${space.slug}`, kind: 'space' as const }
      } else if (section.id === sectionId) {
        return { name: section.title, path: `/${section.slug}`, kind: 'section' as const }
      }
    }
    return null
  }

  /* F-164: the content opens on its page, a status in its space; a refused one opens "Agendados". */
  const scheduledTarget = (n: AppNotification): string | null => {
    if (n.type === POST_FAILED_TYPE) return typeof n.data?.spaceId === 'string' ? `/scheduled?spaceId=${n.data.spaceId}` : '/scheduled'
    const spaceId = typeof n.data?.spaceId === 'string' ? n.data.spaceId : null
    for (const section of sections) {
      const space = spaceId ? section.spaces?.find((sp) => sp.id === spaceId) : null
      if (space) return n.entityType === 'content' && n.entityId ? `/${section.slug}/${space.slug}/${n.entityId}` : `/${section.slug}/${space.slug}`
    }
    return null
  }
  /* F-248: the reminder opens the post; the slugs travel in the notice, the menu covers older ones. */
  const ackReminderTarget = (n: AppNotification): string | null => {
    const { sectionSlug, spaceSlug } = n.data || {}
    if (typeof sectionSlug === 'string' && typeof spaceSlug === 'string' && n.entityId) return `/${sectionSlug}/${spaceSlug}/${n.entityId}`
    return scheduledTarget(n)
  }
  const openAckReminder = (n: AppNotification) => {
    markRead(n)
    const target = ackReminderTarget(n)
    if (!target) return
    setOpen(false)
    navigate(target)
  }
  const openScheduled = (n: AppNotification) => {
    markRead(n)
    const target = scheduledTarget(n)
    if (!target) return
    setOpen(false)
    navigate(target)
  }

  const openUnlocked = (n: AppNotification) => {
    markRead(n)
    const target = unlockedTarget(n)
    if (!target) {
      void refreshMenu({ silent: true })
      return
    }
    setOpen(false)
    navigate(target.path)
  }

  /* F-150: the notice opens Membros › Pendentes. */
  const openSignupRequests = (n: AppNotification) => {
    markRead(n)
    setOpen(false)
    navigate(ROUTES.signupRequests)
  }

  const openAchievements = (n: AppNotification) => {
    markRead(n)
    setOpen(false)
    if (user?.id) openProfile(user.id, 'achievements')
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="relative h-10 w-10 rounded-md text-gray-600 hover:bg-gray-100"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread ? `${t('title')} (${t('unread', { count: unread })})` : t('title')}
        title={t('title')}
        aria-expanded={open}
        data-notification-bell
      >
        <Bell className="h-[18px] w-[18px]" />
        {unread > 0 && (
          <span className="absolute right-1 top-1 inline-flex h-3.5 min-w-[0.875rem] items-center justify-center rounded-full px-[3px] text-[9px] leading-none ring-2 ring-white bg-red-600 font-semibold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Button>
      {open && createPortal(
        <>
          <div className={SIDE_BACKDROP_CLASSES} onClick={() => setOpen(false)} aria-hidden />
          <div
            role="dialog"
            aria-label={t('title')}
            data-notification-panel
            className={cn('fixed inset-0 z-50 flex flex-col bg-white', SIDE_PANEL_CLASSES)}
            {...swipe}
          >
            <div className="flex h-14 shrink-0 items-center gap-2 border-b border-gray-200 px-3">
              <Bell className="h-4 w-4 text-gray-500" />
              <span className="text-base font-semibold text-gray-900">{t('title')}</span>
              {unread > 0 && (
                <Button variant="ghost" size="sm" className="ml-auto text-xs" onClick={markAll}>
                  <CheckCheck className="mr-1 h-3.5 w-3.5" />{t('markAllRead')}
                </Button>
              )}
              <Button variant="ghost" size="icon" className={cn('h-9 w-9', unread === 0 && 'ml-auto')} onClick={() => setOpen(false)} aria-label={t('close')}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            {error && <p className="border-b border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
            <div className={cn('min-h-0 flex-1 overflow-y-auto', SCROLL_CLASSES)}>
              {state === 'loading' && shown.length === 0 && [0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-3" aria-hidden>
                  <div className="h-10 w-10 animate-pulse rounded-full bg-gray-100" />
                  <div className="h-3 w-2/3 animate-pulse rounded bg-gray-100" />
                </div>
              ))}
              {state === 'failed' && shown.length === 0 && (
                <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
                  <AlertTriangle className="h-6 w-6 text-amber-500" />
                  <p className="text-sm text-gray-600">{t('loadError')}</p>
                  <Button size="sm" variant="outline" onClick={load}>{t('retry')}</Button>
                </div>
              )}
              {state === 'ready' && shown.length === 0 && (
                <div className="flex flex-col items-center px-6 py-10 text-center">
                  <Bell className="mb-2 h-8 w-8 text-gray-300" />
                  <p className="text-sm text-gray-600">{t('empty')}</p>
                </div>
              )}
              {shown.map((n) => {
                const name = n.actor?.name || t('someone')
                const requested = n.type === 'chat.connectionRequested'
                if (isChannel(n.type)) {
                  return (
                    <div key={n.id} data-notification={n.type} role="button" onClick={() => openChannelMessage(n)}
                      className={cn('flex gap-3 border-b border-gray-100 px-3 py-3', !n.read && 'bg-primary-50/50')}>
                      <Avatar src={n.actor?.avatar} alt={name} fallback={name} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-900">{channelText(n, name)}</p>
                        <p className="mt-0.5 text-xs text-gray-500">{formatDistanceToNow(new Date(n.createdAt))}</p>
                        <p className="mt-1 text-xs font-medium text-primary">{t('openMessage')}</p>
                      </div>
                      {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-600" aria-hidden />}
                    </div>
                  )
                }
                if (WORKFLOW_TYPES.includes(n.type)) {
                  return (
                    <div key={n.id} data-notification={n.type} role="button" onClick={() => openWorkflow(n)}
                      className={cn('flex gap-3 border-b border-gray-100 px-3 py-3', !n.read && 'bg-primary-50/50')}>
                      <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', n.type === 'workflows.errorRateHigh' ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-600')}>
                        <Workflow className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-900">{workflowText(n)}</p>
                        <p className="mt-0.5 text-xs text-gray-500">{formatDistanceToNow(new Date(n.createdAt))}</p>
                        <p className="mt-1 text-xs font-medium text-primary">{t('openWorkflow')}</p>
                      </div>
                      {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-600" aria-hidden />}
                    </div>
                  )
                }
                if (isOneOnOne(n.type)) {
                  return (
                    <div key={n.id} data-notification={n.type} role="button" onClick={() => openOneOnOne(n)}
                      className={cn('flex gap-3 border-b border-gray-100 px-3 py-3', !n.read && 'bg-primary-50/50')}>
                      <Avatar src={n.actor?.avatar} alt={name} fallback={name} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-900">{t('types.oneOnOneLogged', { name, day: dayMonth(n.data?.date, i18n.language) })}</p>
                        <p className="mt-0.5 text-xs text-gray-500">{formatDistanceToNow(new Date(n.createdAt))}</p>
                        <p className="mt-1 text-xs font-medium text-primary">{t('openMeeting')}</p>
                      </div>
                      {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-600" aria-hidden />}
                    </div>
                  )
                }
                if (SETTINGS_ALERT_TYPES.includes(n.type)) {
                  return <SettingsAlertNotice key={n.id} n={n} onOpen={(path) => { markRead(n); setOpen(false); navigate(path) }} />
                }
                if (n.type === TEAM_NOTICE_TYPE) {
                  const title = typeof n.data?.title === 'string' ? n.data.title : ''
                  const body = typeof n.data?.body === 'string' ? n.data.body : ''
                  return (
                    <div key={n.id} data-notification={n.type} role="button" onClick={() => markRead(n)}
                      className={cn('flex gap-3 border-b border-gray-100 px-3 py-3', !n.read && 'bg-primary-50/50')}>
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary">
                        <Megaphone className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-500">{t('types.teamNotice')}</p>
                        <p className="break-words text-sm font-medium text-gray-900">{title}</p>
                        {body && <p className="mt-0.5 whitespace-pre-line break-words text-sm text-gray-700">{body}</p>}
                        <p className="mt-0.5 text-xs text-gray-500">{formatDistanceToNow(new Date(n.createdAt))}</p>
                      </div>
                      {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-600" aria-hidden />}
                    </div>
                  )
                }
                if (n.type === SIGNUP_REQUEST_TYPE) {
                  const pending = Number(n.data?.pending) || Number(n.data?.count) || 0
                  return (
                    <div key={n.id} data-notification={n.type} role="button" onClick={() => openSignupRequests(n)}
                      className={cn('flex gap-3 border-b border-gray-100 px-3 py-3', !n.read && 'bg-primary-50/50')}>
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600">
                        <UserPlus className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-900">{t('types.signupRequests', { count: Number(n.data?.count) || 1, pending })}</p>
                        <p className="mt-0.5 text-xs text-gray-500">{formatDistanceToNow(new Date(n.createdAt))}</p>
                        <p className="mt-1 text-xs font-medium text-primary">{t('openSignupRequests')}</p>
                      </div>
                      {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-600" aria-hidden />}
                    </div>
                  )
                }
                if (n.type === POST_ACK_REMINDER_TYPE) {
                  const title = typeof n.data?.title === 'string' && n.data.title.trim() ? n.data.title.trim() : t('postAck:bell.untitled')
                  return (
                    <div key={n.id} data-notification={n.type} role="button" onClick={() => openAckReminder(n)}
                      className={cn('flex gap-3 border-b border-border px-3 py-3', !n.read && 'bg-primary-50/50')}>
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                        <ClipboardCheck className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="break-words text-sm text-foreground">{t('postAck:bell.reminder', { title })}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{formatDistanceToNow(new Date(n.createdAt))}</p>
                        <p className="mt-1 text-xs font-medium text-primary">{t('postAck:bell.open')}</p>
                      </div>
                      {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-600" aria-hidden />}
                    </div>
                  )
                }
                if (n.type === POST_PUBLISHED_TYPE || n.type === POST_FAILED_TYPE) {
                  const failedPost = n.type === POST_FAILED_TYPE
                  const title = typeof n.data?.title === 'string' && n.data.title.trim() ? n.data.title.trim() : t('types.scheduledUntitled')
                  const PostIcon = failedPost ? CalendarX : CalendarCheck
                  return (
                    <div key={n.id} data-notification={n.type} role="button" onClick={() => openScheduled(n)}
                      className={cn('flex gap-3 border-b border-border px-3 py-3', !n.read && 'bg-primary-50/50')}>
                      <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', failedPost ? 'bg-amber-50 text-amber-600' : 'bg-primary-50 text-primary-600')}>
                        <PostIcon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-foreground">{t(failedPost ? 'types.scheduleFailed' : 'types.scheduledPublished', { title })}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{formatDistanceToNow(new Date(n.createdAt))}</p>
                        <p className="mt-1 text-xs font-medium text-primary">{t(failedPost ? 'openScheduled' : 'openPost')}</p>
                      </div>
                      {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-600" aria-hidden />}
                    </div>
                  )
                }
                if (n.type === UNLOCKED_TYPE) {
                  const target = unlockedTarget(n)
                  return (
                    <div key={n.id} data-notification={n.type} role="button" onClick={() => openUnlocked(n)}
                      className={cn('flex gap-3 border-b border-border px-3 py-3', !n.read && 'bg-primary-50/50')}>
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                        <Unlock className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-foreground">
                          {target
                            ? t(unlockedMessageKey(target.kind, n.data), { name: target.name })
                            : t('types.unlockedUnknown')}
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{formatDistanceToNow(new Date(n.createdAt))}</p>
                        {target && <p className="mt-1 text-xs font-medium text-primary">{t(target.kind === 'section' ? 'openSection' : 'openSpace')}</p>}
                      </div>
                      {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-600" aria-hidden />}
                    </div>
                  )
                }
                if (BADGE_TYPES.includes(n.type)) {
                  const badge = typeof n.data?.badgeId === 'string' ? myBadges?.get(n.data.badgeId) : undefined
                  return (
                    <div key={n.id} data-notification={n.type} role="button" onClick={() => openAchievements(n)}
                      className={cn('flex gap-3 border-b border-gray-100 px-3 py-3', !n.read && 'bg-primary-50/50')}>
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-50">
                        <BadgeArt src={badge?.imageUrl} size="md" className="h-9 w-9" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-900">{badge ? t('badges:bell.awarded', { name: badge.name }) : t('badges:bell.awardedUnknown')}</p>
                        <p className="mt-0.5 text-xs text-gray-500">{formatDistanceToNow(new Date(n.createdAt))}</p>
                        <p className="mt-1 text-xs font-medium text-primary">{t('badges:bell.open')}</p>
                      </div>
                      {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-600" aria-hidden />}
                    </div>
                  )
                }
                if (isUpvote(n.type)) {
                  return (
                    <div key={n.id} data-notification={n.type} role="button" onClick={() => openTopic(n)}
                      className={cn('flex gap-3 border-b border-gray-100 px-3 py-3', !n.read && 'bg-primary-50/50')}>
                      <Avatar src={n.actor?.avatar} alt={name} fallback={name} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-900">{upvoteText(n, name)}</p>
                        <p className="mt-0.5 text-xs text-gray-500">{formatDistanceToNow(new Date(n.createdAt))}</p>
                        <p className="mt-1 text-xs font-medium text-primary">{t('openTopic')}</p>
                      </div>
                      {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-600" aria-hidden />}
                    </div>
                  )
                }
                return (
                  <div
                    key={n.id}
                    data-notification={n.type}
                    className={cn('flex gap-3 border-b border-gray-100 px-3 py-3', !n.read && 'bg-primary-50/50')}
                    onClick={() => (requested ? markRead(n) : openChat(n))}
                    role={requested ? undefined : 'button'}
                  >
                    <Avatar src={n.actor?.avatar} alt={name} fallback={name} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-900">
                        {requested ? t('types.chatConnectionRequested', { name }) : t('types.chatConnectionAccepted', { name })}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500">{formatDistanceToNow(new Date(n.createdAt))}</p>
                      {requested && (
                        <div className="mt-2 flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" variant="secondary" disabled={busyId === n.id} onClick={() => answer(n, true)} data-notification-accept>{t('accept')}</Button>
                          <Button size="sm" variant="outline" disabled={busyId === n.id} onClick={() => answer(n, false)}>{t('decline')}</Button>
                        </div>
                      )}
                      {!requested && chat?.enabled && <p className="mt-1 text-xs font-medium text-primary">{t('openChat')}</p>}
                    </div>
                    {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-600" aria-hidden />}
                  </div>
                )
              })}
              <InfiniteSentinel hasMore={Boolean(cursor) && !error} loading={loadingMore} onLoadMore={loadMore} count={items.length} />
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  )
}
